/**
 * The product's canonical operator vocabulary.
 *
 * WHY THIS FILE EXISTS
 *
 * A Border Police evaluator sat in front of the COP on 2026-09-02, could not work out how to
 * fly a drone, and said so three ways (#505). The first note on that session reads "Hebrew, or
 * clearer English". This file is the "clearer English" half: the words the product settled on,
 * in one place, so a state cannot be called two things on two screens.
 *
 * It is not a translation layer. It is the decision record, and the guard test
 * (`client/__tests__/lib/copy.test.ts`) enforces it.
 *
 * THE RULES, in the order they are applied:
 *
 *  1. Use the word an operator would say out loud. Not the word the wire uses, not the word
 *     the aviation textbook uses.
 *  2. One word per concept, product-wide. If two screens disagree, both are wrong until one is
 *     chosen here.
 *  3. Abbreviate only what everyone already reads as a word - see ABBREVIATIONS.
 *  4. Say what the button will do, not what the endpoint is called.
 *  5. Never render a wire enum. `in_progress` is not a label. ONE EXCEPTION, and it is named
 *     here so it cannot be mistaken for drift: the dock's four board-command buttons
 *     (`Pre-takeoff`, `Post-takeoff`, `Pre-land`, `Post-land`) ARE their wire verb, because the
 *     person pressing one is matching it against an MQTT log. See `DOCK_COMMAND_LABEL` in
 *     `commands.ts`. The guard test's wire-enum rule only catches `snake_case`, so these pass it
 *     on shape rather than by exemption - if that rule is ever hardened to catch hyphenated or
 *     cased forms, those four are the strings it will trip on, and they are correct as they are.
 *
 * WHAT THIS FILE DOES NOT DECIDE
 *
 * Case. Roughly 326 of the 395 `uppercase` class uses in the client are the
 * `uppercase tracking-*` chrome-label pattern, so a label's capitals come from CSS, not from
 * the string. Strings here are written in sentence case, which renders identically on those
 * surfaces and gives the later Hebrew work (#505 T9) a locale that does not shout. Where a
 * render site has no `uppercase` class, its existing case is left exactly as it was; changing
 * it would be a typographic redesign, which is #505's T1, not this.
 *
 * The one exception is a critical condition, which keeps its capitals in the string itself -
 * `docs/operational-standards.md` requires that, and the guard test enforces the pairing.
 */

/**
 * The canonical noun for each thing an operator deals with.
 *
 * Singular and plural are both given because a sentence that has to pick one at runtime should
 * call `plural()` rather than concatenate an "s".
 */
export const TERMS = {
  /**
   * The flying thing.
   *
   * "drone" over "aircraft", against #505's T8 proposal, which suggested the opposite. Both
   * words are already in the product (68 visible strings say drone, 55 say aircraft, and
   * `DroneWorkspace.tsx` manages to say drone, aircraft and airframe on one screen). "drone" is
   * the plainer of the two and the one a police operator says; "aircraft" is exactly the
   * aviation register #505 asked us to drop where it buys nothing. Code identifiers are not
   * touched by this - only what a person reads.
   */
  drone: 'drone',
  drones: 'drones',

  /** The box the drone lives in. Never "docking station", never "hub", never "nest". */
  dock: 'dock',
  docks: 'docks',

  /**
   * One slot inside a dock, and the door that opens over it.
   *
   * The product currently calls this door a tray in one panel, a bay in another, and a "takeoff
   * phase" in a third. An operator sees a lid open; "bay" is the word for both the slot and its
   * door unless a screen genuinely has to distinguish them, in which case it says so once and
   * never mixes the two.
   */
  bay: 'bay',
  bays: 'bays',

  /** The plan: a saved thing that can be flown more than once. */
  mission: 'mission',
  missions: 'missions',

  /** One recorded takeoff to landing. The history of what happened, not a plan. */
  flight: 'flight',
  flights: 'flights',

  /** The line drawn on the map that a mission follows. */
  route: 'route',
  routes: 'routes',

  /** One point on a route. */
  waypoint: 'waypoint',
  waypoints: 'waypoints',

  /** A place drones operate from. Not "location", not "base". */
  site: 'site',
  sites: 'sites',

  /** A drawn boundary on the map. Not "geofence", not "keep-in", not "area". */
  zone: 'zone',
  zones: 'zones',

  /** A person on record as qualified to fly. The `/fleet/pilots` register. */
  pilot: 'pilot',
  pilots: 'pilots',

  /** A person using this system. The role, and the reader of every string here. */
  operator: 'operator',
  operators: 'operators',

  /** Something the system raised that a person has to look at. Never "alarm". */
  alert: 'alert',
  alerts: 'alerts',

  /** The live picture: the map, what is flying, and what the cameras see. Never "COP". */
  airPicture: 'air picture',

  /** The hand controller. "RC" is allowed as a label; this is the word for prose. */
  controller: 'controller',
} as const;

export type Term = keyof typeof TERMS;

/**
 * Words that used to appear on screen and must not come back, with what replaced them.
 *
 * The guard test fails on any of these appearing as a standalone word in a string under
 * `lib/copy/`. Keys are matched case-insensitively on a word boundary.
 *
 * This is the half of the vocabulary decision that is enforceable. `TERMS` says what to write;
 * this says what a reviewer no longer has to argue about.
 */
export const RETIRED_TERMS: Readonly<Record<string, string>> = {
  aircraft: 'drone',
  airframe: 'drone',
  'a/c': 'drone',
  uav: 'drone',
  uas: 'drone',
  'docking station': 'dock',
  hub: 'dock',
  tray: 'bay',
  sortie: 'flight (or say plainly that nothing is in the air)',
  alarm: 'alert',
  cop: 'air picture',
  'common operating picture': 'air picture',
  geofence: 'zone',
  'keep-in': 'zone',
  loiter: 'hover',
  deconflict: 'check for conflicts',
  'stand down': 'cancel',
  'on station': 'in position',
  handback: 'hand back',
  agl: 'above ground',
  msl: 'above sea level',
  ack: 'acknowledge',
  maint: 'maintenance',
  dispatch: 'send',
  deploy: 'send',
} as const;

/**
 * The only abbreviations allowed to stand alone in an operator-facing string.
 *
 * Each earns its place by being read as a word rather than expanded: an operator says "R T H",
 * not "return to home", and every one of these appears on the aircraft's own controller or in
 * a regulation. Everything else spells itself out. `A/C`, `ACK`, `AGL`, `MAINT` and `GO-TO`
 * are specifically not here.
 *
 * Regulatory and product names are separately allowed because they are proper nouns, not
 * abbreviations we chose.
 */
export const ABBREVIATIONS: ReadonlySet<string> = new Set([
  /**
   * Not an abbreviation at all - it is the marking on the controller, and the word a crew says
   * for the thing. It sits in this set because the guard test's rule is "a standalone run of
   * capitals must be a term we allow", and this is one.
   */
  'ARM',
  'RTH',
  'GPS',
  'RC',
  'UTC',
  'ID',
  'URL',
  'API',
]);

/** Proper nouns that look like abbreviations to the guard test but are names. */
export const PROPER_NOUNS: ReadonlySet<string> = new Set([
  'SHOVRON',
  'DJI',
  'FAA',
  'NDAA',
  'CJIS',
  'TAK',
  'ATAK',
  'CoT',
  'MQTT',
  'MAVLink',
  'HLS',
  'RTMP',
  'WebRTC',
  'MFA',
  'TOTP',
  'EASA',
  'CASA',
  'TCCA',
  'CAAI',
  'RemoteID',
  'AirScort',
  /**
   * Airspace restrictions, as the FAA names them on the chart an operator is already reading.
   * Spelling either one out ("temporary flight restriction") would be our words for a thing
   * that has an official name, and the map legend has room for neither.
   */
  'TFR',
  'NOTAM',
]);

/**
 * `${count} ${noun}`, with the noun agreeing with the count.
 *
 * Exists because six places in the client render `{n} drones` unguarded, so a fleet of one
 * reads "1 sites, 1 drones, 1 pilots registered" (`fleet/page.tsx`). Five other files guard it
 * correctly by hand, which is how we know the omission was accidental rather than a choice.
 *
 * Named `one`/`other` after the ICU plural categories on purpose: English needs two forms, and
 * when #505's T9 turns these strings into a dictionary, an ICU message is a mechanical
 * translation of this call rather than a rewrite of the call site.
 */
export function plural(count: number, one: string, other: string): string {
  return `${count} ${count === 1 ? one : other}`;
}

/** The agreeing noun alone, for sentences that place the count somewhere else. */
export function pluralWord(count: number, one: string, other: string): string {
  return count === 1 ? one : other;
}
